import moment, { type Moment } from "moment";
import type { Client } from "#/types/client";
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from "#/config/dateTimeFormats";

/**
 * Centralized date/time formatting utilities.
 *
 * These functions should be used throughout the application instead of
 * calling moment.format() directly. This ensures all date/time displays
 * respect the client's configured format preferences.
 */

/**
 * Get the date format for a client, or default
 */
export const getDateFormat = (client: Client | null | undefined): string => {
  return client?.dateFormat || DEFAULT_DATE_FORMAT;
};

/**
 * Get the time format for a client, or default
 */
export const getTimeFormat = (client: Client | null | undefined): string => {
  return client?.timeFormat || DEFAULT_TIME_FORMAT;
};

/**
 * Format a date according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const dateFormat = getDateFormat(client);
  return moment.utc(date).local().format(dateFormat);
};

/**
 * Format a time according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted time string
 */
export const formatTime = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const timeFormat = getTimeFormat(client);
  return moment.utc(date).local().format(timeFormat);
};

/**
 * Format a date and time according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const dateFormat = getDateFormat(client);
  const timeFormat = getTimeFormat(client);
  return moment.utc(date).local().format(`${dateFormat} ${timeFormat}`);
};

/**
 * Format a date and time for export (CSV, etc.)
 * Always uses UTC and a standard format
 *
 * @param date - Date to format
 * @returns Formatted date/time string for export
 */
export const formatDateTimeForExport = (
  date: string | Date | Moment | null | undefined
): string => {
  if (!date) return "";
  return moment.utc(date).format("DD/MM/YYYY HH:mm");
};
